import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

const EMOTION_EMOJI = {
  Happy: '😊', Sad: '😢', Angry: '😠', Surprised: '😮', Scared: '😨', Neutral: '😐',
};

function ReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const reportRef = useRef(null);

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    setIsLoading(true);
    fetch(`${API_URL}/api/report/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed to load report');
        return r.json();
      })
      .then(data => setReport(data))
      .catch(err => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [sessionId, token]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getStageName = (stage) => {
    if (stage === 'stage1') return 'Pre-Test — Emotion Quiz';
    if (stage === 'stage2') return 'Training — Emotion Detection';
    if (stage === 'pretest') return 'Pre-Test — Emotion Quiz';
    if (stage === 'posttest') return 'Post-Test — Emotion Quiz';
    if (stage === 'training') return 'Training — Emotion Detection';
    if (/^training_(\d+)$/.test(stage)) {
      const n = stage.match(/^training_(\d+)$/)[1];
      return `Training — Session ${n}`;
    }
    return stage;
  };

  const formatDate = (iso) => new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const getPctColor = (pct) =>
    pct >= 80 ? '#059669' : pct >= 60 ? '#D97706' : '#DC2626';

  const fmtTime = (ms) => {
    if (ms == null) return '—';
    const totalSec = ms / 1000;
    if (totalSec < 60) return `${totalSec.toFixed(1)}s`;
    const m = Math.floor(totalSec / 60);
    const s = Math.round(totalSec % 60);
    return `${m}m ${String(s).padStart(2, '0')}s`;
  };

  const getGrade = (pct) =>
    pct === 100 ? '🌟 Perfect'
    : pct >= 80  ? '👏 Excellent'
    : pct >= 60  ? '💪 Good'
    : pct >= 40  ? '📚 Keep Practising'
    : '🔁 Needs More Practice';

  // ── PDF Download ─────────────────────────────────────────────────────────

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setIsPdfLoading(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#F0F4F8',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let y = 0;
      let remaining = imgH;
      while (remaining > 0) {
        if (y > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -y, imgW, imgH);
        y += pageH;
        remaining -= pageH;
      }
      const childName = report?.session?.child_name || 'child';
      pdf.save(`Feelio_Report_${childName.replace(/\s+/g, '_')}.pdf`);
    } finally {
      setIsPdfLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="home-shell" style={{ paddingTop: '90px' }}>

      {/* Header */}
      <header className="home-header">
        <div className="home-brand">
          <span onClick={() => navigate(-1)} style={{ cursor: 'pointer', fontSize: '20px', marginRight: '4px' }}>←</span>
          <span className="home-logo-icon">📊</span>
          <span className="home-brand-name">Child Report</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {report && (
            <button
              onClick={downloadPdf}
              disabled={isPdfLoading}
              style={{
                background: isPdfLoading ? '#86EFAC' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                border: 'none', borderRadius: '12px', padding: '8px 16px',
                fontSize: '14px', fontWeight: '700', color: '#fff', cursor: isPdfLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Segoe UI, Inter, sans-serif',
              }}
            >
              {isPdfLoading ? '⏳ Generating...' : '⬇️ Download PDF'}
            </button>
          )}
          <button onClick={() => navigate('/')} style={s.homeBtn}>🏠 Home</button>
        </div>
      </header>

      {isLoading && (
        <p style={{ color: '#6B7280', marginTop: '60px', fontSize: '16px' }}>⏳ Loading report...</p>
      )}

      {error && (
        <div style={s.errorBox}>⚠️ {error}</div>
      )}

      {report && (
        <div ref={reportRef} style={{ width: '100%', maxWidth: '800px', display: 'flex', flexDirection: 'column', gap: '24px', padding: '16px 0' }}>

          {/* ── Session info ── */}
          <div style={s.card}>
            <h2 style={s.cardTitle}>👶 Child Details</h2>
            <div style={s.infoGrid}>
              <InfoRow label="Name"     value={report.session.child_name} />
              <InfoRow label="Age"      value={`${report.session.child_age} years`} />
              <InfoRow label="Hospital" value={report.session.hospital_location} />
              <InfoRow label="Practitioner" value={report.session.practitioner_name} />
              <InfoRow label="Session Date" value={formatDate(report.session.created_at)} />
              {report.session.provisional_diagnosis && (
                <InfoRow label="Diagnosis" value={report.session.provisional_diagnosis} />
              )}
              {report.session.language && (
                <InfoRow label="Language" value={report.session.language} />
              )}
            </div>
          </div>

          {/* ── No results yet ── */}
          {report.results.length === 0 && (
            <div style={{ ...s.card, textAlign: 'center', color: '#9CA3AF', padding: '40px' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
              <p style={{ margin: 0, fontSize: '15px' }}>No quiz results yet for this session.</p>
            </div>
          )}

          {/* ── One card per stage result ── */}
          {report.results.map((result, ri) => {
            const pct = Math.round((result.score / result.total) * 100);

            // Emotion-wise breakdown
            const emotionStats = {};
            result.answers.forEach(a => {
              if (!emotionStats[a.correct_emotion]) {
                emotionStats[a.correct_emotion] = { correct: 0, total: 0 };
              }
              emotionStats[a.correct_emotion].total += 1;
              if (a.is_correct) emotionStats[a.correct_emotion].correct += 1;
            });

            return (
              <div key={result.id || ri} style={s.card}>
                {/* Stage header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                  <h2 style={s.cardTitle}>
                    🎮 {getStageName(result.stage)}
                  </h2>
                  <span style={{ fontSize: '13px', color: '#9CA3AF' }}>{formatDate(result.completed_at)}</span>
                </div>

                {/* Score summary */}
                <div style={s.scoreSummary}>
                  <div style={s.scoreBig}>
                    <span style={{ fontSize: '48px', fontWeight: '900', color: getPctColor(pct) }}>{result.score}</span>
                    <span style={{ fontSize: '28px', color: '#9CA3AF', fontWeight: '700' }}>/{result.total}</span>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', fontWeight: '900', color: getPctColor(pct) }}>{pct}%</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#4B5563', marginTop: '4px' }}>{getGrade(pct)}</div>
                  </div>

                  {/* Total time */}
                  {result.total_time_ms != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: '700', color: '#4B5563' }}>
                      <span>⏱️ Total time:</span>
                      <span style={{ color: '#1E40AF', fontSize: '18px' }}>{fmtTime(result.total_time_ms)}</span>
                    </div>
                  )}

                  {/* Progress bar */}
                  <div style={{ width: '100%' }}>
                    <div style={{ height: '14px', background: '#E5E7EB', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: getPctColor(pct), borderRadius: '99px', transition: 'width 0.8s ease' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                      <span>0%</span><span>100%</span>
                    </div>
                  </div>
                </div>

                {/* Emotion-wise stats */}
                <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1A2340', margin: '24px 0 12px' }}>
                  Emotion Performance
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', marginBottom: '24px' }}>
                  {Object.entries(emotionStats).map(([emotion, stat]) => {
                    const epct = Math.round((stat.correct / stat.total) * 100);
                    return (
                      <div key={emotion} style={{ ...s.emotionStat, borderColor: epct === 100 ? '#86EFAC' : epct >= 50 ? '#FCD34D' : '#FCA5A5', background: epct === 100 ? '#F0FDF4' : epct >= 50 ? '#FFFBEB' : '#FFF5F5' }}>
                        <span style={{ fontSize: '28px' }}>{EMOTION_EMOJI[emotion] || '😐'}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#1A2340' }}>{emotion}</span>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: getPctColor(epct) }}>{stat.correct}/{stat.total}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Answer-by-answer table */}
                <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1A2340', margin: '0 0 12px' }}>
                  Question-by-Question
                </h3>
                <div style={s.answerTable}>
                  <div style={s.answerHead}>
                    <span style={{ width: '40px' }}>#</span>
                    <span style={{ flex: 1 }}>Correct Answer</span>
                    <span style={{ flex: 1 }}>Child's Answer</span>
                    <span style={{ width: '80px', textAlign: 'center' }}>Time</span>
                    <span style={{ width: '50px', textAlign: 'center' }}>Result</span>
                  </div>
                  {result.answers.map((a, i) => (
                    <div key={i} style={{ ...s.answerRow, background: i % 2 === 0 ? '#fff' : '#F9FAFB' }}>
                      <span style={{ width: '40px', color: '#9CA3AF', fontSize: '13px' }}>Q{i + 1}</span>
                      <span style={{ flex: 1, fontWeight: '600', color: '#059669' }}>
                        {EMOTION_EMOJI[a.correct_emotion]} {a.correct_emotion}
                      </span>
                      <span style={{ flex: 1, fontWeight: '600', color: a.is_correct ? '#059669' : '#DC2626' }}>
                        {EMOTION_EMOJI[a.selected_emotion]} {a.selected_emotion}
                      </span>
                      <span style={{ width: '80px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#6B7280' }}>
                        {fmtTime(a.time_taken_ms)}
                      </span>
                      <span style={{ width: '50px', textAlign: 'center', fontSize: '18px' }}>
                        {a.is_correct ? '✅' : '❌'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: '12px', fontSize: '14px', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ width: '130px', color: '#6B7280', fontWeight: '600', flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#1A2340', fontWeight: '700' }}>{value}</span>
    </div>
  );
}

const s = {
  homeBtn: {
    background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: '12px',
    padding: '8px 16px', fontSize: '14px', fontWeight: '700', color: '#2563EB', cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  errorBox: {
    background: '#FFF5F5', border: '2px solid #FCA5A5', borderRadius: '12px',
    padding: '14px 20px', fontSize: '14px', fontWeight: '600', color: '#991B1B',
    maxWidth: '800px', width: '100%',
  },
  card: {
    background: '#fff', borderRadius: '20px', padding: '28px 32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB',
  },
  cardTitle: { fontSize: '20px', fontWeight: '800', color: '#1A2340', margin: '0 0 16px' },
  infoGrid: { display: 'flex', flexDirection: 'column' },
  scoreSummary: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
    background: '#F8FAFC', borderRadius: '16px', padding: '24px',
  },
  scoreBig: { display: 'flex', alignItems: 'baseline', gap: '4px' },
  emotionStat: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    padding: '12px 8px', borderRadius: '12px', border: '2px solid',
  },
  answerTable: {
    borderRadius: '12px', overflow: 'hidden', border: '1px solid #E5E7EB',
  },
  answerHead: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
    background: '#1E40AF', color: '#fff', fontSize: '12px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.4px',
  },
  answerRow: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 16px', fontSize: '14px', borderBottom: '1px solid #F3F4F6',
  },
};

export default ReportPage;
